import type { CSSProperties, ReactNode } from 'react'
import { Emblem, type EmblemName } from './brand/Emblems'
import { rarityClass } from './rarity'
import { cx } from './cx'

export type CellItem = {
  /** Picture or emblem of the item. */
  art: ReactNode
  name: string
  rarity?: string
  quantity?: number
  equipped?: boolean
}

type ItemCellProps = {
  item?: CellItem
  selected?: boolean
  onSelect?: () => void
  /** Double press: the item's main action (put on, drink) — a shortcut next to the card's button. */
  onActivate?: () => void
  className?: string
}

/** Satchel cell: rarity frame, stack count, equipped mark. Empty cells are inert. */
export function ItemCell({ item, selected, onSelect, onActivate, className }: ItemCellProps) {
  if (!item) return <div className={cx('cellslot is-empty', className)} aria-hidden="true" />
  const label = `${item.name}${item.quantity && item.quantity > 1 ? `, ${item.quantity} шт.` : ''}${item.equipped ? ', надето' : ''}`
  return (
    <button
      type="button"
      className={cx('cellslot', rarityClass(item.rarity), selected && 'is-selected', className)}
      aria-pressed={selected}
      aria-label={label}
      onClick={onSelect}
      onDoubleClick={onActivate}
    >
      {item.art}
      {item.equipped && <span className="eq" aria-hidden="true" />}
      {item.quantity !== undefined && item.quantity > 1 && <span className="qty" aria-hidden="true">{item.quantity}</span>}
    </button>
  )
}

type GearSlotProps = {
  label: string
  emptyEmblem: EmblemName
  item?: { art: ReactNode; name: string; rarity?: string; stale?: boolean }
  selected?: boolean
  locked?: boolean
  onSelect?: () => void
  style?: CSSProperties
  tagStyle?: CSSProperties
  className?: string
}

/** Paper-doll cell with its caption. Positions come from the doll layout. */
export function GearSlot({ label, emptyEmblem, item, selected, locked, onSelect, style, tagStyle, className }: GearSlotProps) {
  const filled = Boolean(item)
  const aria = `${label}: ${item ? item.name + (item.stale ? ' (не действует)' : '') : 'пусто'}`
  return (
    <>
      <button
        type="button"
        className={cx('gslot gslot--s', filled ? `is-filled ${rarityClass(item?.rarity)}` : 'is-locked', selected && 'is-selected', item?.stale && 'is-stale', className)}
        style={style}
        aria-label={aria}
        aria-pressed={selected}
        disabled={locked}
        onClick={onSelect}
      >
        {item ? item.art : <Emblem name={emptyEmblem} />}
      </button>
      <div className={cx('gslot__tag', filled && 'is-gold')} style={tagStyle} aria-hidden="true">{label}</div>
    </>
  )
}
